// Copyright © 2026 ApexRuntime Labs by Shashi (https://github.com/spshah). All rights reserved.

import { useState, useEffect, useCallback } from 'react';
import { Plus, Edit2, Trash2, AlertCircle, X, Zap, Sparkles } from 'lucide-react';
import categoryAPI from '../api/services/budget/categoryService';
import subcategoryAPI from '../api/services/budget/subcategoryService';
import testDataAPI from '../api/services/testDataService';
import CategoryForm from '../components/categories/CategoryForm';

const CATEGORY_TYPE_COLORS = {
  EXPENSE: 'bg-red-100 text-red-800',
  INCOME: 'bg-green-100 text-green-800',
  SAVING: 'bg-blue-100 text-blue-800',
  TRANSFER: 'bg-gray-100 text-gray-800',
};

const CATEGORY_TYPE_LABELS = {
  EXPENSE: 'Expense',
  INCOME: 'Income',
  SAVING: 'Saving',
  TRANSFER: 'Transfer',
};

export default function CategoriesPage() {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [formLoading, setFormLoading] = useState(false);
  const [testDataLoading, setTestDataLoading] = useState(false);
  const [alert, setAlert] = useState({
    show: false,
    message: '',
    type: 'success',
  });
  const [filter, setFilter] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  // Load categories
  const loadCategories = useCallback(async () => {
    setLoading(true);
    try {
      const response = await categoryAPI.list({ active: 'true' });
      setCategories(response.data || []);
    } catch (error) {
      showAlert(
        error.response?.data?.message || 'Failed to load categories',
        'error'
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadCategories();
  }, [loadCategories]);

  const showAlert = (message, type = 'success') => {
    setAlert({ show: true, message, type });
    setTimeout(() => setAlert((a) => ({ ...a, show: false })), 4000);
  };

  const handleClearTestData = async () => {
    if (!window.confirm('Delete all categories? This cannot be undone.')) return;

    setTestDataLoading(true);
    try {
      const result = await testDataAPI.deleteTestCategories();
      showAlert(
        result.message,
        result.success ? 'success' : 'error'
      );
      loadCategories();
    } catch (err) {
      showAlert('Failed to delete categories', 'error');
      console.error(err);
    } finally {
      setTestDataLoading(false);
    }
  };

  const handlePopulateTestData = async () => {
    setTestDataLoading(true);
    try {
      const result = await testDataAPI.populateTestCategories();
      showAlert(
        result.message,
        result.success ? 'success' : 'error'
      );
      loadCategories();
    } catch (err) {
      showAlert('Failed to populate test data', 'error');
      console.error(err);
    } finally {
      setTestDataLoading(false);
    }
  };

  const handleGenerateTestTransactions = async () => {
    setTestDataLoading(true);
    try {
      const result = await testDataAPI.populateTestTransactions();
      showAlert(
        result.message,
        result.success ? 'success' : 'error'
      );
    } catch (err) {
      showAlert('Failed to generate test transactions', 'error');
      console.error(err);
    } finally {
      setTestDataLoading(false);
    }
  };

  const handleCreate = () => {
    setEditingCategory(null);
    setShowForm(true);
  };

  const handleEdit = (category) => {
    setEditingCategory(category);
    setShowForm(true);
  };

  const handleSubmit = async (formData) => {
    setFormLoading(true);
    try {
      if (editingCategory) {
        await categoryAPI.update(editingCategory.id, formData);
        showAlert('Category updated successfully');
      } else {
        await categoryAPI.create(formData);
        showAlert('Category created successfully');
      }
      setShowForm(false);
      loadCategories();
    } catch (error) {
      showAlert(
        error.response?.data?.message || 'Failed to save category',
        'error'
      );
    } finally {
      setFormLoading(false);
    }
  };

  const handleDelete = async (item) => {
    const isSubcategory = item.category !== undefined;
    const itemType = isSubcategory ? 'Subcategory' : 'Category';

    if (!window.confirm(`Delete ${itemType} "${item.name}"?`)) return;

    try {
      if (isSubcategory) {
        await subcategoryAPI.delete(item.id);
      } else {
        await categoryAPI.delete(item.id);
      }
      showAlert(`${itemType} deleted successfully`);
      loadCategories();
    } catch (error) {
      showAlert(
        error.response?.data?.message || `Failed to delete ${itemType}`,
        'error'
      );
    }
  };

  // Filter & search
  const filtered = categories.filter((cat) => {
    const matchesType = !filter || cat.category_type === filter;
    const matchesSearch =
      !searchTerm ||
      cat.name.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesType && matchesSearch;
  });

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Categories</h1>
          <p className="text-gray-600 text-sm mt-1">
            Create and manage your spending categories
          </p>
        </div>
        <div className="flex gap-2">
          {/* Dev Delete Button */}
          <button
            onClick={handleClearTestData}
            disabled={testDataLoading}
            className="flex items-center gap-2 px-3 py-2 bg-red-500 text-white text-sm rounded-lg hover:bg-red-600 disabled:opacity-60 disabled:cursor-not-allowed"
            title="[DEV] Delete all test categories"
          >
            <Trash2 size={18} />
            {testDataLoading ? 'Deleting...' : 'Clear'}
          </button>
          {/* Dev Test Categories Button */}
          <button
            onClick={handlePopulateTestData}
            disabled={testDataLoading}
            className="flex items-center gap-2 px-3 py-2 bg-yellow-500 text-white text-sm rounded-lg hover:bg-yellow-600 disabled:opacity-60 disabled:cursor-not-allowed"
            title="[DEV] Populate test categories with sample data"
          >
            <Zap size={18} />
            {testDataLoading ? 'Loading...' : 'Test Categories'}
          </button>
          {/* Dev Test Transactions Button */}
          <button
            onClick={handleGenerateTestTransactions}
            disabled={testDataLoading}
            className="flex items-center gap-2 px-3 py-2 bg-purple-500 text-white text-sm rounded-lg hover:bg-purple-600 disabled:opacity-60 disabled:cursor-not-allowed"
            title="[DEV] Generate ~100 random test transactions across last year using existing categories"
          >
            <Sparkles size={18} />
            {testDataLoading ? 'Generating...' : 'Test Transactions'}
          </button>
          <button
            onClick={handleCreate}
            className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
          >
            <Plus size={20} />
            New Category
          </button>
        </div>
      </div>

      {/* Alert */}
      {alert.show && (
        <div
          className={`mb-6 p-4 rounded-lg flex items-start justify-between ${
            alert.type === 'success'
              ? 'bg-green-50 border border-green-200 text-green-800'
              : 'bg-red-50 border border-red-200 text-red-800'
          }`}
        >
          <div className="flex items-center gap-2">
            <AlertCircle size={18} />
            <span>{alert.message}</span>
          </div>
          <button
            onClick={() => setAlert((a) => ({ ...a, show: false }))}
            className="text-gray-400 hover:text-gray-600"
          >
            <X size={16} />
          </button>
        </div>
      )}

      {/* Filters */}
      <div className="flex gap-4 mb-6">
        <input
          type="text"
          placeholder="Search categories..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
        />
        <select
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
        >
          <option value="">All Types</option>
          <option value="EXPENSE">Expense</option>
          <option value="INCOME">Income</option>
          <option value="SAVING">Saving</option>
          <option value="TRANSFER">Transfer</option>
        </select>
      </div>

      {/* Loading State */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-12">
          <AlertCircle className="mx-auto h-12 w-12 text-gray-400 mb-4" />
          <p className="text-gray-600">
            {categories.length === 0
              ? 'No categories yet. Create one to get started!'
              : 'No categories match your search'}
          </p>
        </div>
      ) : (
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <p className="text-xs font-bold text-gray-400 mb-4 uppercase">
            Category Hierarchy
          </p>
          <div className="font-mono text-sm space-y-2">
            {filtered.map((category) => (
              <div key={category.id} className="space-y-1">
                {/* Parent Category */}
                <div
                  className="flex justify-between items-center px-2 py-2 rounded hover:bg-gray-50 group transition-colors"
                  onMouseEnter={(e) => {
                    const btns = e.currentTarget.querySelector('[data-action-buttons]');
                    if (btns) btns.style.display = 'flex';
                  }}
                  onMouseLeave={(e) => {
                    const btns = e.currentTarget.querySelector('[data-action-buttons]');
                    if (btns) btns.style.display = 'none';
                  }}
                >
                  <div className="flex items-center gap-3 flex-1">
                    <span className="text-gray-600">
                      {category.icon || '📁'} {category.name}
                    </span>
                    <span
                      className={`inline-block text-xs px-2 py-1 rounded ${
                        CATEGORY_TYPE_COLORS[category.category_type]
                      }`}
                    >
                      {CATEGORY_TYPE_LABELS[category.category_type]}
                    </span>
                  </div>
                  <div
                    data-action-buttons
                    className="flex gap-2 items-center"
                    style={{ display: 'none' }}
                  >
                    <button
                      onClick={() => handleEdit(category)}
                      className="p-1.5 text-blue-600 hover:bg-blue-50 rounded transition-colors"
                      title="Edit category"
                    >
                      <Edit2 size={16} />
                    </button>
                    <button
                      onClick={() => handleDelete(category)}
                      className="p-1.5 text-red-600 hover:bg-red-50 rounded transition-colors"
                      title="Delete category"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>

                {/* Subcategories */}
                {category.subcategories && category.subcategories.length > 0 && (
                  <div className="space-y-1">
                    {category.subcategories.map((sub, idx) => (
                      <div
                        key={sub.id}
                        className="flex justify-between items-center px-2 py-2 pl-8 rounded hover:bg-red-50 group transition-colors"
                        onMouseEnter={(e) => {
                          const btn = e.currentTarget.querySelector('[data-delete-btn]');
                          if (btn) btn.style.display = 'flex';
                        }}
                        onMouseLeave={(e) => {
                          const btn = e.currentTarget.querySelector('[data-delete-btn]');
                          if (btn) btn.style.display = 'none';
                        }}
                      >
                        <div className="flex items-center gap-2">
                          <span className="text-gray-500">
                            └── {sub.icon || '📋'} {sub.name}
                          </span>
                        </div>
                        <button
                          data-delete-btn
                          onClick={() => handleDelete(sub)}
                          className="text-red-600 hover:text-red-700 p-1 rounded hover:bg-red-100 transition-colors"
                          style={{ display: 'none' }}
                          title="Delete subcategory"
                        >
                          <X size={16} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Form Modal */}
      {showForm && (
        <CategoryForm
          category={editingCategory}
          onSubmit={handleSubmit}
          onCancel={() => setShowForm(false)}
          loading={formLoading}
        />
      )}
    </div>
  );
}
